import { QuotaExceededErrorItem } from "../interfaces/error"
import { t } from "../lib/i18n"

// auth-api は code を **`"#{product_name}_quota_exceeded"`** で組み立てる
// （#1215 の Billing::PlanDowngradeGate）。**だから code を閉じた集合で
// 分岐しないこと。** 商品が増えれば code も自動で増えるので、列挙した瞬間に
// 「新しい商品では何も出ない」が生まれる。
const CODE_SUFFIX = "_quota_exceeded"

export const isQuotaExceededCode = (code: unknown): code is string =>
  typeof code === "string" && code.endsWith(CODE_SUFFIX) && code !== CODE_SUFFIX

// `metadata.product_name` が正。無いときは code から復元する。
//
// **#1182（content_type）は metadata に product_name を載せていません**（実測）。
// content_type だけを特別扱いすると、同じ形の商品がもう 1 つ出たときにまた
// 落ちるので、**suffix を剥がす一般規則**にしてある。#1182 に product_name が
// 入っても、この関数は metadata を優先するのでそのまま動く。
export const productNameOf = (error: QuotaExceededErrorItem): string =>
  error.metadata?.product_name || error.code.slice(0, -CODE_SUFFIX.length)

// t() は未知のキーを `::key::` で返す。その場合は生の product 名を出す。
// **黙らないことが目的**なので、何も出ないより読める英字の方がよい。
const productLabel = (productName: string): string => {
  const translated = t(productName)

  return translated.startsWith("::") ? productName : translated
}

// エラーオブジェクトに積まれた内訳を取り出す。**instanceof を使わない**：
// target: es5 で Error を継承しているため、ビルド系によって instanceof の
// 結果が変わりうる。「この値が載っているか」だけで判定する。
export const quotaExceededErrorsOf = (
  error: unknown,
): QuotaExceededErrorItem[] => {
  const items = (error as { quotaErrors?: unknown } | null | undefined)
    ?.quotaErrors

  if (!Array.isArray(items)) return []

  return items.filter(
    (item): item is QuotaExceededErrorItem =>
      !!item && isQuotaExceededCode((item as QuotaExceededErrorItem).code),
  )
}

// レスポンスの body（`{ errors: [...] }`）から内訳を組み立てる。
// 読めなければ空配列 —— 呼び出し側は従来どおり汎用のエラーに落ちる。
export const parseQuotaExceededErrors = (
  body: unknown,
): QuotaExceededErrorItem[] => {
  const errors = (body as { errors?: unknown } | null | undefined)?.errors

  if (!Array.isArray(errors)) return []

  return errors.filter(
    (item): item is QuotaExceededErrorItem =>
      !!item && isQuotaExceededCode((item as QuotaExceededErrorItem).code),
  )
}

// ---------------------------------------------------------------------------
// ⚠️ 文言はここ 1 箇所に閉じてある。**まだ仮です。**
//
// Notion の確認事項 L27（CLOUD のプラン表示名）/ C23（移行期間の表示）/
// X-9（「未計測」の文言）が未決で、どれも同じ「ユーザーに見せる語」の系列。
// 決定が出たら **この下の 3 つを差し替えるだけ**で済むようにしてある
// （cms-api #2815 の QuotaDisplayHelper::QUOTA_NOT_MEASURED_TEXT と同じ形）。
// ---------------------------------------------------------------------------
export const QUOTA_EXCEEDED_TITLE =
  "上限を超えているため、プランを変更できません"

const lineWithNumbers = (label: string, current: number, limit: number) =>
  `${label}: 現在 ${current} / 変更後の上限 ${limit}。${label}を減らしてから変更してください。`

const lineWithoutNumbers = (label: string) =>
  `${label}: 変更後のプランの上限を超えています。${label}を減らしてから変更してください。`

export const quotaExceededLine = (error: QuotaExceededErrorItem): string => {
  const label = productLabel(productNameOf(error))
  const current = error.metadata?.current
  const limit = error.metadata?.limit

  if (typeof current !== "number" || typeof limit !== "number") {
    return lineWithoutNumbers(label)
  }

  return lineWithNumbers(label, current, limit)
}

export const quotaExceededDescription = (
  errors: QuotaExceededErrorItem[],
): string => errors.map(quotaExceededLine).join("\n")
